// Copyright (c) 2021 WiseTime. All rights reserved.

import { flatten, partition, uniq } from "ramda"
import {
  PostStatus,
  TimeGroup,
  TimeGroup_NarrativeType,
  TimeRow,
} from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { Site } from "../../generated/legebill/site/v4/site_pb"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { TagCategoryRef } from "../../generated/wisetime/user/type/tag_category_ref_pb"
import { SelectedTimesheetFieldOption } from "../../generated/wisetime/user/type/selected_timesheet_field_option_pb"
import { DEFAULT_ROUNDING_PREFERENCE_SECONDS } from "./roundingPreferences"

export type CollapsibleTimeGroup = {
  timeGroup: TimeGroup,
  timeRows: TimeRow[],
}

// TODO: Remove type cast once proto inconsistency is fixed
// The proto files should be updated to reuse the shared legebill.type.NarrativeType enum
// instead of having TimeGroup.NarrativeType as a nested enum.
// Currently:
//   - Site.postTimePrefs.narrativeType is of type legebill.type.NarrativeType
//   - CreateTimeGroupRequest.narrativeType expects wisetime.user.timeline.v4.TimeGroup.NarrativeType
// Both enums have identical numeric values (0, 1, 2), so this cast is safe but shouldn't be necessary.
export const narrativeType = (currentSite: Site | undefined): TimeGroup_NarrativeType =>
  (currentSite?.postTimePrefs?.narrativeType ?? TimeGroup_NarrativeType.NARRATIVE_ONLY) as TimeGroup_NarrativeType

export const secondsToHours = (seconds: number) => Number((seconds / secondsInHour).toFixed(2))

export const secondsInHour = 3_600
type DurationStep = number
type FractionDigits = 1 | 2

/**
 * Converts seconds to hours with rounding based on the specified rounding preference.
 * Uses ceiling rounding to ensure time is never rounded down.
 * 
 * @param seconds - Duration in seconds to convert
 * @param roundingPreferenceSeconds - Rounding preference in seconds (e.g., 360 for 6-minute increments)
 * @returns Duration in hours, rounded up to the nearest rounding increment
 * 
 * @example
 * secondsToHoursRounded(1800, 360) // 1800s = 0.5h, rounds up to 0.6h (next 6-min increment)
 * secondsToHoursRounded(3600, 360) // 3600s = 1.0h, stays at 1.0h (already on increment)
 */
export const secondsToHoursRounded = (
  seconds: number,
  roundingPreferenceSeconds: number,
): number => {
  const durationStep = computeDurationStep(roundingPreferenceSeconds)
  const fractionDigits = computeFractionDigits(roundingPreferenceSeconds)
  const units = (seconds / secondsInHour) / durationStep
  // Round to 6 decimal places before ceiling to avoid floating point precision issues
  const roundedUnits = Number(units.toFixed(6))
  const number = Math.ceil(roundedUnits) * durationStep
  return Number(number.toFixed(fractionDigits))
}

/**
 * Calculates the total rounded hours for multiple time groups.
 * Each time group's duration is individually rounded, then summed and rounded again.
 * 
 * @param timeGroups - Array of time groups to sum
 * @param roundingPreferenceSeconds - Rounding preference in seconds
 * @returns Total duration in hours, rounded according to preference
 */
export const totalHoursRounded = (
  timeGroups: TimeGroup[],
  roundingPreferenceSeconds: number,
): number => {
  const totalDurationInSecs = timeGroups.reduce(
    (result, { totalDuration }) =>
      result + (secondsToHoursRounded(Number(totalDuration?.seconds ?? 0), roundingPreferenceSeconds) * secondsInHour),
    0,
  )
  return secondsToHoursRounded(totalDurationInSecs, roundingPreferenceSeconds)
}

// Round fractional seconds
export const secondsRounded = (seconds: number): number => Math.round(seconds)
export const hoursToSecondsRounded = (hours: string): number => secondsRounded(Number(hours) * secondsInHour)

/**
 * Computes the duration step in hours based on rounding preference in seconds.
 * Provides defensive fallback to default rounding preference for invalid inputs.
 * 
 * @param roundingPreferenceSeconds - Rounding preference in seconds
 * @returns Duration step in hours (e.g., 0.1 for 6-minute increments)
 * 
 * @example
 * computeDurationStep(360) // Returns 0.1 (360 seconds = 6 minutes = 0.1 hours)
 * computeDurationStep(900) // Returns 0.25 (900 seconds = 15 minutes = 0.25 hours)
 * computeDurationStep(0) // Returns 0.1 (fallback to default)
 * computeDurationStep(-100) // Returns 0.1 (fallback to default)
 */
export const computeDurationStep = (roundingPreferenceSeconds: number): DurationStep => {
  if (roundingPreferenceSeconds <= 0) {
    return DEFAULT_ROUNDING_PREFERENCE_SECONDS / secondsInHour
  }
  // Convert seconds to hours to get the duration step
  const hoursStep = roundingPreferenceSeconds / secondsInHour
  return Number(hoursStep.toFixed(2))
}

/**
 * Determines the number of decimal places needed for displaying durations.
 * Returns 2 digits for fine-grained steps (e.g., 0.05h), 1 digit for coarse steps (e.g., 0.1h).
 * 
 * @param roundingPreferenceSeconds - Rounding preference in seconds
 * @returns Number of decimal places (1 or 2)
 * 
 * @example
 * computeFractionDigits(360) // Returns 1 (0.1h increments need 1 decimal place)
 * computeFractionDigits(180) // Returns 2 (0.05h increments need 2 decimal places)
 */
export const computeFractionDigits = (roundingPreferenceSeconds: number): FractionDigits => {
  const durationStep = computeDurationStep(roundingPreferenceSeconds)
  // Use epsilon comparison to handle floating point precision issues
  // This checks if the step requires more than 1 decimal place for proper display.
  return (Math.abs((durationStep * 10) % 1) > Number.EPSILON) ? 2 : 1
}

/**
 * Increments a duration value by one rounding step.
 * Used for UI controls like time picker up arrows.
 * 
 * @param hours - Current duration in hours
 * @param roundingPreferenceSeconds - Rounding preference in seconds
 * @returns Incremented duration with appropriate precision
 * 
 * @example
 * increment(1.0, 360) // Returns 1.1 (adds 0.1h for 6-minute increments)
 * increment(1.0, 900) // Returns 1.25 (adds 0.25h for 15-minute increments)
 */
export const increment = (hours: number, roundingPreferenceSeconds: number): number => {
  const durationStep = computeDurationStep(roundingPreferenceSeconds)
  const fractionDigits = computeFractionDigits(roundingPreferenceSeconds)
  return Number((Number(hours) + durationStep).toFixed(fractionDigits))
}

/**
 * Decrements a duration value by one rounding step, with minimum value protection.
 * Used for UI controls like time picker down arrows.
 * 
 * @param hours - Current duration in hours
 * @param roundingPreferenceSeconds - Rounding preference in seconds
 * @param min - Minimum allowed value (defaults to 0)
 * @returns Decremented duration, clamped to minimum value
 * 
 * @example
 * decrement(1.1, 360) // Returns 1.0 (subtracts 0.1h for 6-minute increments)
 * decrement(0.1, 360) // Returns 0.0 (clamped to minimum)
 */
export const decrement = (hours: number, roundingPreferenceSeconds: number, min = 0): number => {
  const durationStep = computeDurationStep(roundingPreferenceSeconds)
  const fractionDigits = computeFractionDigits(roundingPreferenceSeconds)
  const decrementedValue = Number((hours - durationStep).toFixed(fractionDigits))
  return decrementedValue < min ? min : decrementedValue
}

export type TimeGroupListItem = {
  id: string,
  narrative: string,
  tags: TagRef[],
  tagCategories: TagCategoryRef[],
}
export const matchTimeGroupIdList = (
  timeRows: TimeRow[],
  timeGroupListItems: TimeGroupListItem[],
  currentSiteId: string | undefined,
): string[] => {
  const uniqFlatten = (nestedIdsLists: string[][]): string[] => uniq(flatten(nestedIdsLists))
  const idListForCurrentSite = <T extends { id: string, siteId: string }>(items: T[]): string[] =>
    items.reduce(
      (result: string[], { id, siteId }: T) => {
        if (siteId === currentSiteId) {
          result.push(id)
        }
        return result
      },
      [],
    )
  const tagIdsFromTimeRows = uniqFlatten(timeRows.map(t => idListForCurrentSite(t.tags)))
  const tagCategoryIdsFromTimeRows = uniqFlatten(timeRows.map(t => idListForCurrentSite(t.tagCategories)))
  if (tagIdsFromTimeRows.length === 0 && tagCategoryIdsFromTimeRows.length === 0) {
    return []
  }
  return timeGroupListItems.reduce(
    (result: string[], { tags, tagCategories, id }: TimeGroupListItem) => {
      if ((tagIdsFromTimeRows.length === 0 || tagIdsFromTimeRows.every(id => tags.some(t => t.id === id)))
        && (tagCategoryIdsFromTimeRows.length === 0 || tagCategoryIdsFromTimeRows.every(id => tagCategories.some(t => t.id === id)))
      ) {
        result.push(id)
      }
      return result
    },
    [],
  )
}

export const isDisabledTimeGroup = (postStatus: PostStatus) =>
  postStatus === PostStatus.SUCCESS
  || postStatus === PostStatus.PENDING
  || postStatus === PostStatus.TRANSIENT_FAILURE

type SplitTimeGroupsByPostStatus = {
  posted: TimeGroup[],
  unposted: TimeGroup[],
}
export const splitTimeGroupsByPostStatus = (
  timeGroups: TimeGroup[],
): SplitTimeGroupsByPostStatus => {
  const [posted, unposted] = partition((timeGroup) => isDisabledTimeGroup(timeGroup.postStatus), timeGroups)
  return { posted, unposted }
}

type TimesheetFieldOptionsBySiteId = Record<string, SelectedTimesheetFieldOption[]>

export const groupPostedTimesheetFieldOptionsBySiteId = (
  postedTimeGroups: TimeGroup[],
): TimesheetFieldOptionsBySiteId => {
  const postedSelectedOptions = flatten(
    postedTimeGroups.map(timeGroup => (
      timeGroup.selectedTimesheetFieldOptions.filter(o => o.siteId === timeGroup.postedToSiteId)
    ))
  )
  return postedSelectedOptions.reduce((result, option) => {
    if (!result[option.siteId]) {
      result[option.siteId] = []
    }
    result[option.siteId]?.push(option)
    return result
  }, {} as TimesheetFieldOptionsBySiteId)
}
export const groupTimesheetFieldOptionsBySiteId = (
  timeRows: TimeRow[],
): TimesheetFieldOptionsBySiteId => {
  const options = flatten(
    timeRows.map(timeGroup => (
      timeGroup.selectedTimesheetFieldOptions
    ))
  )
  return options.reduce((result, option) => {
    if (!result[option.siteId]) {
      result[option.siteId] = []
    }
    result[option.siteId]?.push(option)
    return result
  }, {} as TimesheetFieldOptionsBySiteId)
}

export const groupUnpostedTimesheetFieldOptionsBySiteId = (
  unpostedTimeGroups: TimeGroup[],
): TimesheetFieldOptionsBySiteId => {
  const unpostedSelectedOptions = flatten(
    unpostedTimeGroups.map(timeGroup => timeGroup.selectedTimesheetFieldOptions)
  )
  return unpostedSelectedOptions.reduce((result, option) => {
    if (!result[option.siteId]) {
      result[option.siteId] = []
    }
    result[option.siteId]?.push(option)
    return result
  }, {} as TimesheetFieldOptionsBySiteId)
}

export const groupTimeRowTimesheetFieldOptionsBySiteId = (
  timeRows: TimeRow[],
): TimesheetFieldOptionsBySiteId => {
  const timeRowSelectedOptions = flatten(
    timeRows.map(timeRow => timeRow.selectedTimesheetFieldOptions)
  )
  return timeRowSelectedOptions.reduce((result, option) => {
    if (!result[option.siteId]) {
      result[option.siteId] = []
    }
    result[option.siteId]?.push(option)
    return result
  }, {} as TimesheetFieldOptionsBySiteId)
}
